import React, { useEffect, useRef, useState } from 'react';

import {
  DisplayPlacementId,
  PlacementStatus
} from 'node_modules/@schibsted-nmp/advertising-shared/src/types/api/index.js';
import type { LayoutType } from 'node_modules/@schibsted-nmp/advertising-shared/src/types/layouts/search.js';

import { updateMetricByKey } from '@client/core/atoms/metrics.js';
import { useInScreen } from '@client/core/hooks/index.js';
import {
  BapSearchAd,
  AdImage
} from '@schibsted-nmp/search-frontend-components/react';

import { DsaModal } from '../DsaModal.js';
import {
  trackMartailerClick,
  trackMartailerImpression,
  type MartailerProduct
} from '../utils/MartailerApi.js';

export type SimpleBapSearchAdProps = {
  layout?: LayoutType;
  onClick?: () => void;
  products: Array<MartailerProduct>;
};

export type LabelRenderProps = {
  className?: string;
  classes: {
    badge: string;
    info: string;
    warning: string;
    success: string;
    [key: string]: string;
  };
};

export function SponsoredProductAd({
  layout,
  onClick,
  products
}: SimpleBapSearchAdProps) {
  const product = products[0];
  const [isModalOpen, setIsModalOpen] = useState(false);

  const adRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);

  // Track viewability
  const { hasIntersected } = useInScreen({
    ref: adRef,
    threshold: 0.5,
    rootMargin: '0px'
  });

  useEffect(() => {
    if (hasIntersected && !hasTrackedView.current && product) {
      console.log('Ad viewed');
      hasTrackedView.current = true;
      updateMetricByKey(
        DisplayPlacementId.AdvtSplMiddle1,
        PlacementStatus.AdViewed
      );

      void trackMartailerImpression({
        adId: product.adId,
        itemId: product.itemId,
        placement: 'home',
        visitorId: 'test-user',
        category: product.category,
        store: 'finn_recommerce'
      });
    }
  }, [hasIntersected, product]);

  const handleClick = () => {
    console.log('Ad clicked');

    if (product) {
      void trackMartailerClick({
        adId: product.adId,
        itemId: product.itemId,
        placement: 'home',
        visitorId: 'test-user',
        category: product.category,
        store: 'finn_recommerce'
      });
    }

    onClick?.();
  };

  if (!product) return null;

  const bapAd = convertToBapFormat(product);

  const IMAGE_SIZES =
    '(min-width: 1300px) 306px, (min-width: 1010px) 316px, (min-width: 768px) 33vw, (min-width: 480px) 50vw, 100vw';

  return (
    <>
      <div ref={adRef}>
        <BapSearchAd
          ad={bapAd}
          id={bapAd.ad_id}
          imageSizes={IMAGE_SIZES}
          layout={layout}
          onClick={handleClick}
          url={bapAd.canonical_url}
          className="sponsored-product-ad"
          label={
            layout === 'list'
              ? () => (
                  <div
                    className="className"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: '20px',
                      padding: '6px',
                      zIndex: 10
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <span
                      style={{
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#474445'
                      }}
                    >
                      Reklame
                    </span>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsModalOpen(true);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#0063FB',
                        fontSize: '12px',
                        fontWeight: 400,
                        cursor: 'pointer',
                        padding: 0
                      }}
                    >
                      Ad info
                    </button>
                  </div>
                )
              : undefined
          }
          image={
            layout === 'grid'
              ? () => (
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        backgroundColor: 'white',
                        zIndex: 1
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <span
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#1B1B1F'
                        }}
                      >
                        Reklame
                      </span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsModalOpen(true);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#0063FB',
                          textDecoration: 'none',
                          fontSize: '12px',
                          fontWeight: 300,
                          cursor: 'pointer',
                          padding: 0,
                          position: 'relative',
                          zIndex: 2
                        }}
                      >
                        Ad info
                      </button>
                    </div>
                    <AdImage
                      aspectClass="aspect-3/4"
                      imageClass="s-bg"
                      height={bapAd.image.height}
                      width={bapAd.image.width}
                      url={bapAd.image.url}
                      crop={false}
                      sizes={IMAGE_SIZES}
                    />
                  </div>
                )
              : undefined
          }
        />
      </div>

      <DsaModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        product={product}
      />
    </>
  );
}

function optimizeImageUrl(url: string): string {
  const params = new URLSearchParams({
    w: '320',
    q: '30',
    f: 'webp'
  });
  return `${url}?${params.toString()}`;
}

function convertToBapFormat(product: MartailerProduct) {
  return {
    ad_id: product.adId,
    canonical_url: product.link,
    distance: null,
    extras: [],
    flags: [],
    heading: product.title,
    image: {
      aspect_ratio: 1.3333333333333333,
      height: 400,
      url: optimizeImageUrl(product.imageLink),
      width: 533
    },
    labels: [],
    location: '',
    main_search_key: 'SEARCH_ID_BAP_COMMON',
    organisation_name: product.organisationName,
    price: {
      currency_code: 'NOK',
      amount: product.price,
      price_unit: 'kr'
    },
    timestamp: undefined, // Try this
    trade_type: 1
  };
}
